/**
 * Date handling.
 *
 * `scheduled_date` is a bare calendar day (`YYYY-MM-DD`), never an instant. It
 * is deliberately *not* parsed with `new Date(string)`, which would interpret it
 * as UTC midnight and shift it a day for anyone west of Greenwich. Everything
 * here works on the plain string or on an explicitly-constructed local date.
 *
 * Formatting follows the active language. The locale is read per call rather
 * than captured once: switching language has to change the dates already on
 * screen, and a module-level constant would keep printing the old one.
 */

import { getIntlLocale } from '@/lib/i18n/store';
import { translate } from '@/lib/i18n/translate';

/** Parse `YYYY-MM-DD` into a Date at local midnight — no timezone shift. */
export function parseDay(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1);
}

/** Format a Date as `YYYY-MM-DD` using its *local* fields. */
export function toDayString(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addDays(day: string, count: number): string {
  const date = parseDay(day);
  date.setDate(date.getDate() + count);
  return toDayString(date);
}

/** Whole days from `from` to `to`. Negative means `to` is in the past. */
export function daysBetween(from: string, to: string): number {
  const start = parseDay(from).getTime();
  const end = parseDay(to).getTime();
  return Math.round((end - start) / 86_400_000);
}

/** "24 August" / "24. srpna" / "24. August" — the Today header. */
export function formatDayLong(day: string): string {
  return parseDay(day).toLocaleDateString(getIntlLocale(), {
    day: 'numeric',
    month: 'long',
  });
}

/** "Mon 24 Aug" / "po 24. 8." — compact, for task rows. */
export function formatDayShort(day: string): string {
  return parseDay(day).toLocaleDateString(getIntlLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
  });
}

/**
 * A heading for one day in Upcoming, relative to today where that reads better.
 *
 * Within the coming week a weekday name ("Thursday") is more useful than a
 * date; beyond it, the date is.
 */
export function formatUpcomingHeading(day: string, today: string): string {
  const delta = daysBetween(today, day);
  if (delta === 1) return translate('date.tomorrow');
  if (delta > 1 && delta < 7) {
    return capitalise(
      parseDay(day).toLocaleDateString(getIntlLocale(), { weekday: 'long' }),
    );
  }
  return parseDay(day).toLocaleDateString(getIntlLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * How late a task is, phrased for the overdue banner.
 *
 * `Intl.RelativeTimeFormat` rather than hand-built phrases: "před 3 dny",
 * "vor 3 Tagen" and "3 days ago" decline differently, and the platform already
 * knows how. `numeric: 'auto'` is what turns -1 day into "yesterday".
 */
export function formatOverdueAge(day: string, today: string): string {
  const late = daysBetween(day, today);
  const relative = new Intl.RelativeTimeFormat(getIntlLocale(), { numeric: 'auto' });
  if (late < 7) return relative.format(-late, 'day');
  if (late < 60) return relative.format(-Math.floor(late / 7), 'week');
  return formatDayShort(day);
}

/** "14:32" or "24. 8. 14:32" if it is not today — for comments and history. */
export function formatTimestamp(iso: string, referenceDay?: string): string {
  const value = new Date(iso);
  const sameDay = referenceDay ? toDayString(value) === referenceDay : false;
  return sameDay
    ? value.toLocaleTimeString(getIntlLocale(), { hour: '2-digit', minute: '2-digit' })
    : value.toLocaleString(getIntlLocale(), {
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
